(() => {
  'use strict';
  if (window.__unisystemMotionReady) return;
  window.__unisystemMotionReady = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const headers = new Map();
  const heroes = new Map();
  const carousels = new Map();
  const figures = new Map();
  const revealed = new WeakSet();
  const modalSubscribers = new Set();
  let modalOpen = !!document.querySelector('dialog[open]');
  const syncModalState = () => {
    const next = !!document.querySelector('dialog[open]');
    if (next === modalOpen) return;
    modalOpen = next;
    modalSubscribers.forEach(refresh => refresh());
  };
  ['close', 'cancel', 'toggle'].forEach(type => document.addEventListener(type, syncModalState, true));
  if (typeof MutationObserver === 'function') {
    const modalObserver = new MutationObserver(records => {
      if (records.some(record => record.type === 'attributes' && record.target.matches('dialog') || record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches('dialog') || node.querySelector('dialog'))))) syncModalState();
    });
    modalObserver.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'], childList: true });
  }

  const motionAllowed = element => !reducedMotion.matches && document.body.dataset.motion !== 'none' && !element.closest('[data-motion="none"]');
  const within = (scope, selector) => [
    ...(scope instanceof Element && scope.matches(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];

  function initHeader(header) {
    if (headers.has(header)) return;
    const abort = new AbortController();
    const listen = { signal: abort.signal };
    const section = header.closest('.shopify-section') || header;
    const utility = header.querySelector('.uni-utility');
    const nav = header.querySelector('[data-nav-track]');
    const mobileHeader = window.matchMedia('(max-width: 989px)');
    const scrollPosition = () => Math.max(0, Math.min(window.scrollY, document.documentElement.scrollHeight - window.innerHeight));
    let lastScroll = scrollPosition();
    let scrollDirection = 0;
    let scrollTravel = 0;
    let hovered = null;
    let frame = null;
    const triggers = nav ? [...nav.querySelectorAll('.uni-nav--grouped > a,.uni-nav--grouped > details > summary,.uni-header__students')] : [];
    const triggerFor = element => triggers.find(trigger => trigger === element || trigger.contains(element) || trigger.parentElement?.matches('.uni-nav-group') && trigger.parentElement.contains(element));

    const setHeaderHidden = hidden => {
      if (section.classList.contains('is-scroll-hidden') === hidden) return;
      section.classList.toggle('is-scroll-hidden', hidden);
      document.dispatchEvent(new CustomEvent('uni:headerchange', { detail: { hidden } }));
    };
    const updateScroll = () => {
      frame = null;
      const currentScroll = scrollPosition();
      const delta = currentScroll - lastScroll;
      lastScroll = currentScroll;
      header.classList.toggle('is-compact', currentScroll > (utility?.offsetHeight || 32) + 8);
      const focused = header.contains(document.activeElement) && document.activeElement?.matches(':focus-visible');
      if (!mobileHeader.matches || currentScroll <= section.offsetHeight + 24 || header.querySelector('dialog[open]') || focused) {
        scrollTravel = 0;
        scrollDirection = 0;
        setHeaderHidden(false);
        return;
      }
      if (Math.abs(delta) < 1) return;
      const direction = Math.sign(delta);
      scrollTravel = direction === scrollDirection ? scrollTravel + Math.abs(delta) : Math.abs(delta);
      scrollDirection = direction;
      if (scrollTravel >= (direction > 0 ? 12 : 8)) {
        setHeaderHidden(direction > 0);
        scrollTravel = 0;
      }
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(updateScroll);
    };
    const pointTo = link => {
      if (!nav) return;
      if (!link || !link.getClientRects().length || nav.offsetWidth === 0 || !header.querySelector('.uni-nav--grouped')?.offsetWidth) {
        nav.classList.remove('is-pointing');
        return;
      }
      const box = link.getBoundingClientRect(), trackBox = nav.getBoundingClientRect();
      nav.style.setProperty('--uni-nav-x', `${box.left - trackBox.left - nav.clientLeft}px`);
      nav.style.setProperty('--uni-nav-y', `${box.top - trackBox.top - nav.clientTop}px`);
      nav.style.setProperty('--uni-nav-width', `${box.width}px`);
      nav.style.setProperty('--uni-nav-height', `${box.height}px`);
      nav.classList.add('is-pointing');
    };
    const restorePoint = () => {
      if (!nav) return;
      const focused = nav.contains(document.activeElement) ? triggerFor(document.activeElement) : null;
      const opened = triggers.find(trigger => trigger.matches('summary') && trigger.parentElement.open);
      const current = triggers.find(trigger => trigger.matches('[aria-current="page"]') || trigger.parentElement?.matches('.uni-nav-group') && trigger.parentElement.querySelector('[aria-current="page"]'));
      pointTo(focused || hovered || opened || current);
    };
    const measure = () => {
      section.style.setProperty('--uni-utility-offset', `${utility?.offsetHeight || 0}px`);
      restorePoint();
      updateScroll();
    };

    if (nav) {
      nav.classList.add('is-tracked');
      triggers.forEach(link => {
        link.addEventListener('pointerenter', () => {
          if (!finePointer.matches) return;
          hovered = link;
          pointTo(link);
        }, listen);
      });
      nav.addEventListener('pointerleave', () => { hovered = null; restorePoint(); }, listen);
      nav.addEventListener('focusin', event => pointTo(triggerFor(event.target)), listen);
      nav.addEventListener('toggle', restorePoint, { ...listen, capture: true });
      nav.addEventListener('focusout', () => window.requestAnimationFrame(restorePoint), listen);
    }
    window.addEventListener('scroll', onScroll, { ...listen, passive: true });
    window.addEventListener('resize', measure, { ...listen, passive: true });
    mobileHeader.addEventListener('change', measure, listen);
    header.addEventListener('toggle', onScroll, { ...listen, capture: true });
    header.addEventListener('focusin', () => { scrollTravel = 0; setHeaderHidden(false); }, listen);
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (utility) resize?.observe(utility);
    if (nav) resize?.observe(nav);
    measure();
    headers.set(header, () => {
      abort.abort();
      resize?.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      setHeaderHidden(false);
    });
  }

  function createAutoplay(root, advance, listen, canAdvance = () => true, retainManualSelection = false) {
    const button = root.querySelector('[data-carousel-pause]');
    const interval = Math.min(10000, Math.max(4000, Number(root.dataset.carouselInterval || document.body.dataset.carouselInterval) || 5000));
    const enabled = root.dataset.carouselAutoplay !== 'false' && document.body.dataset.carouselAutoplay !== 'false';
    const editor = !!window.Shopify?.designMode;
    let visible = false;
    let hovered = false;
    let focused = root.contains(document.activeElement);
    let touchFocus = false;
    let paused = false;
    let manualUntil = 0;
    let timer = null;
    let disposed = false;
    let cycle = 0;
    const stop = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      root.classList.remove('is-autoplay-running');
    };
    const allowed = () => enabled && !editor && motionAllowed(root) && !document.hidden && visible && !hovered && !focused && !paused && !document.querySelector('dialog[open]') && canAdvance();
    const updateButton = () => {
      if (!button) return;
      button.hidden = !enabled || editor || !motionAllowed(root) || !canAdvance();
      button.classList.toggle('is-paused', paused);
      button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    };
    const schedule = () => {
      stop();
      updateButton();
      if (disposed || !allowed()) return;
      const delay = Math.max(interval, manualUntil - Date.now());
      root.style.setProperty('--uni-carousel-duration', `${delay}ms`);
      root.style.setProperty('--uni-carousel-keyframe', ++cycle % 2 ? 'uni-carousel-clock' : 'uni-carousel-clock-next');
      root.classList.add('is-autoplay-running');
      timer = window.setTimeout(async () => {
        timer = null;
        root.classList.remove('is-autoplay-running');
        if (disposed || !allowed()) return;
        await advance();
        schedule();
      }, delay);
    };
    modalSubscribers.add(schedule);
    const manual = () => {
      if (retainManualSelection) paused = true;
      manualUntil = Date.now() + Math.max(12000, interval * 2);
      schedule();
    };
    button?.addEventListener('click', () => { paused = !paused; schedule(); }, listen);
    root.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && finePointer.matches) { hovered = true; schedule(); } }, listen);
    root.addEventListener('pointerleave', () => { hovered = false; schedule(); }, listen);
    root.addEventListener('pointerdown', event => {
      touchFocus = event.pointerType === 'touch' || event.pointerType === 'pen';
      if (touchFocus) { focused = false; manual(); }
    }, listen);
    document.addEventListener('keydown', () => {
      touchFocus = false;
      focused = root.contains(document.activeElement);
      schedule();
    }, listen);
    root.addEventListener('focusin', () => { focused = !touchFocus; schedule(); }, listen);
    root.addEventListener('focusout', event => { focused = !touchFocus && root.contains(event.relatedTarget); schedule(); }, listen);
    document.addEventListener('visibilitychange', schedule, listen);
    reducedMotion.addEventListener('change', schedule, listen);
    document.addEventListener('uni:motionchange', schedule, listen);
    root.addEventListener('shopify:block:select', manual, listen);
    const view = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .2);
      schedule();
    }, { threshold: [0, .2] }) : null;
    if (view) view.observe(root);
    else { visible = true; schedule(); }
    updateButton();
    return { manual, refresh: schedule, canRun: allowed, dispose: () => { disposed = true; stop(); view?.disconnect(); modalSubscribers.delete(schedule); } };
  }

  function bindHeroSwipe(track, advance, manual, listen) {
    let origin = null;
    track.addEventListener('dragstart', event => { if (event.target.closest('img')) event.preventDefault(); }, listen);
    track.addEventListener('pointerdown', event => {
      if (event.isPrimary === false || event.button !== 0 || event.target.closest('a,button,input,select,textarea,video')) return;
      origin = { x: event.clientX, y: event.clientY, id: event.pointerId, horizontal: false };
      manual();
    }, listen);
    track.addEventListener('pointermove', event => {
      if (!origin || origin.id !== event.pointerId) return;
      const x = event.clientX - origin.x;
      const y = event.clientY - origin.y;
      if (!origin.horizontal && Math.abs(y) > 12 && Math.abs(y) > Math.abs(x)) { origin = null; return; }
      if (Math.abs(x) > 12 && Math.abs(x) > Math.abs(y) * 1.25) {
        origin.horizontal = true;
        if (event.cancelable) event.preventDefault();
        if (!track.hasPointerCapture?.(event.pointerId)) track.setPointerCapture?.(event.pointerId);
        track.classList.add('is-dragging');
      }
    }, { ...listen, passive: false });
    const end = event => {
      if (!origin || origin.id !== event.pointerId) return;
      const x = event.clientX - origin.x;
      const complete = event.type !== 'pointercancel' && origin.horizontal && Math.abs(x) > 42;
      origin = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture?.(event.pointerId);
      if (complete) advance(x < 0 ? 1 : -1);
    };
    track.addEventListener('pointerup', end, listen);
    track.addEventListener('pointercancel', end, listen);
  }

  function initHero(hero) {
    if (heroes.has(hero)) return;
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const controls = hero.querySelector('[data-hero-controls]');
    const pills = [...hero.querySelectorAll('[data-hero-go]')];
    const status = hero.querySelector('[data-hero-status]');
    const track = hero.querySelector('.uni-hero__slides');
    if (slides.length < 2 || !controls) return;
    const abort = new AbortController();
    const listen = { signal: abort.signal };
    const prepared = new Map();
    let current = 0;
    let requested = 0;
    let requestVersion = 0;
    const prepare = index => {
      if (prepared.has(index)) return prepared.get(index);
      const slide = slides[index];
      const template = slide.querySelector('template[data-hero-image]');
      if (template) {
        const fragment = template.content.cloneNode(true);
        fragment.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
        template.replaceWith(fragment);
      }
      const images = [...slide.querySelectorAll('img')];
      const ready = Promise.all(images.map(img => typeof img.decode === 'function' ? img.decode().catch(() => undefined) : Promise.resolve()));
      prepared.set(index, ready);
      return ready;
    };
    const show = async (index, announce = true, guard = null) => {
      const target = (index + slides.length) % slides.length;
      requested = target;
      const version = ++requestVersion;
      controls.setAttribute('aria-busy', 'true');
      await prepare(target);
      if (version !== requestVersion || !hero.isConnected) return;
      if (guard && !guard()) { requested = current; controls.removeAttribute('aria-busy'); return; }
      slides.forEach((slide, i) => {
        const active = i === target;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      pills.forEach((pill, i) => pill.setAttribute('aria-pressed', String(i === target)));
      if (announce && target !== current && status) {
        const label = slides[target].dataset.slideLabel;
        status.textContent = `${target + 1} / ${slides.length}${label ? ` · ${label}` : ''}`;
      }
      current = target;
      controls.removeAttribute('aria-busy');
    };
    const autoplay = createAutoplay(hero, () => show(requested + 1, false, () => autoplay.canRun()), listen);
    const manualShow = index => { autoplay.manual(); return show(index); };
    const measureControls = () => hero.style.setProperty('--uni-controls-height', `${controls.offsetHeight}px`);
    controls.hidden = false;
    hero.querySelector('[data-hero-prev]')?.addEventListener('click', () => manualShow(requested - 1), listen);
    hero.querySelector('[data-hero-next]')?.addEventListener('click', () => manualShow(requested + 1), listen);
    pills.forEach((pill, index) => {
      pill.addEventListener('click', () => manualShow(index), listen);
      pill.addEventListener('focus', () => prepare(index), listen);
      pill.addEventListener('pointerenter', () => { if (finePointer.matches) prepare(index); }, listen);
    });
    controls.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || !event.target.closest('button') || event.target.closest('[data-carousel-pause]')) return;
      let next;
      if (event.key === 'ArrowLeft') next = requested - 1;
      else if (event.key === 'ArrowRight') next = requested + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = slides.length - 1;
      else return;
      event.preventDefault();
      manualShow(next);
      if (event.target.matches('[data-hero-go]')) pills[(next + slides.length) % slides.length].focus();
    }, listen);
    if (track) bindHeroSwipe(track, direction => manualShow(requested + direction), autoplay.manual, listen);
    hero.addEventListener('shopify:block:select', event => {
      const index = slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
      if (index >= 0) show(index, false);
    }, listen);
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measureControls) : null;
    resize?.observe(controls);
    measureControls();
    heroes.set(hero, () => { requestVersion += 1; autoplay.dispose(); abort.abort(); resize?.disconnect(); });
  }

  function initCarousel(root) {
    if (carousels.has(root)) return;
    const track = root.querySelector('[data-carousel-track]');
    const controls = root.querySelector('[data-carousel-controls]');
    if (!track || !controls || track.children.length < 2) return;
    const abort = new AbortController();
    const listen = { signal: abort.signal };
    const items = [...track.children];
    const position = controls.querySelector('[data-carousel-position]');
    const progress = controls.querySelector('[data-carousel-progress]');
    const status = controls.querySelector('[data-carousel-status]');
    let scrollFrame = null;
    let drag = null;
    let suppressClick = false;
    const maximum = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const overflows = () => maximum() > 4;
    const indexAt = () => {
      const left = track.scrollLeft;
      const start = items[0].offsetLeft;
      return items.reduce((closest, item, i) => Math.abs(item.offsetLeft - start - left) < Math.abs(items[closest].offsetLeft - start - left) ? i : closest, 0);
    };
    const update = () => {
      scrollFrame = null;
      const index = indexAt();
      if (position) position.textContent = `${String(index + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
      if (progress) progress.style.setProperty('--uni-carousel-progress', maximum() ? String(Math.min(1, Math.max(0, track.scrollLeft / maximum()))) : '1');
    };
    const scrollTo = (left, announce = false) => {
      track.scrollTo({ left, behavior: motionAllowed(root) ? 'smooth' : 'auto' });
      if (announce && status) {
        const start = items[0].offsetLeft;
        const target = items.reduce((best, item, i) => Math.abs(item.offsetLeft - start - left) < Math.abs(items[best].offsetLeft - start - left) ? i : best, 0);
        status.textContent = `${target + 1} / ${items.length}`;
      }
    };
    const advance = (direction, announce = false) => {
      const start = items[0].offsetLeft;
      const left = track.scrollLeft;
      let destination;
      if (direction > 0) {
        if (left >= maximum() - 4) destination = 0;
        else destination = Math.min(maximum(), items.find(item => item.offsetLeft - start > left + 8)?.offsetLeft - start || maximum());
      } else if (left <= 4) destination = maximum();
      else destination = Math.max(0, [...items].reverse().find(item => item.offsetLeft - start < left - 8)?.offsetLeft - start || 0);
      scrollTo(destination, announce);
    };
    const hasReadingContent = root.hasAttribute('data-language-route') || [...track.querySelectorAll('figcaption')].some(caption => caption.textContent.trim());
    const autoplay = createAutoplay(root, () => advance(1), listen, () => overflows() && !root.querySelector('dialog[open]'), hasReadingContent);
    const measure = () => { controls.hidden = !overflows(); root.classList.toggle('has-carousel-overflow', overflows()); update(); autoplay.refresh(); };
    controls.querySelector('[data-carousel-prev]')?.addEventListener('click', () => { autoplay.manual(); advance(-1, true); }, listen);
    controls.querySelector('[data-carousel-next]')?.addEventListener('click', () => { autoplay.manual(); advance(1, true); }, listen);
    track.addEventListener('scroll', () => { if (scrollFrame === null) scrollFrame = window.requestAnimationFrame(update); }, { ...listen, passive: true });
    track.addEventListener('wheel', autoplay.manual, { ...listen, passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey || !overflows()) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); autoplay.manual();
      if (event.key === 'Home') scrollTo(0, true);
      else if (event.key === 'End') scrollTo(maximum(), true);
      else advance(event.key === 'ArrowRight' ? 1 : -1, true);
    }, listen);
    track.addEventListener('dragstart', event => { if (overflows() && event.target.closest('img,a')) event.preventDefault(); }, listen);
    track.addEventListener('pointerdown', event => {
      autoplay.manual();
      if (event.pointerType !== 'mouse' || event.button !== 0 || !overflows() || event.target.closest('button,input,select,textarea')) return;
      drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, active: false };
      suppressClick = false;
    }, listen);
    track.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const distance = event.clientX - drag.x;
      if (!drag.active && Math.abs(distance) < 8) return;
      drag.active = true;
      suppressClick = true;
      if (event.cancelable) event.preventDefault();
      track.setPointerCapture?.(event.pointerId);
      track.classList.add('is-dragging');
      track.scrollLeft = drag.left - distance;
    }, { ...listen, passive: false });
    const endDrag = event => {
      if (!drag || drag.id !== event.pointerId) return;
      const dragged = drag.active;
      drag = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture?.(event.pointerId);
      if (dragged) scrollTo(Math.min(maximum(), items[indexAt()].offsetLeft - items[0].offsetLeft));
    };
    track.addEventListener('pointerup', endDrag, listen);
    track.addEventListener('pointercancel', event => { endDrag(event); suppressClick = false; }, listen);
    track.addEventListener('click', event => { if (suppressClick) { event.preventDefault(); event.stopPropagation(); suppressClick = false; } }, { ...listen, capture: true });
    const dialog = root.querySelector('dialog');
    dialog?.addEventListener('close', autoplay.refresh, listen);

    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    resize?.observe(track);
    window.addEventListener('resize', measure, { ...listen, passive: true });
    root.addEventListener('shopify:block:select', event => {
      const item = event.target.closest('.uni-gallery-item, .uni-language__level');
      if (item && track.contains(item)) scrollTo(Math.min(maximum(), item.offsetLeft - items[0].offsetLeft));
    }, listen);
    measure();
    carousels.set(root, () => { autoplay.dispose(); abort.abort(); resize?.disconnect(); if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame); });
  }

  function initFigures(scope) {
    within(scope, '[data-count-up], [data-method-progress]').forEach(element => {
      if (figures.has(element)) return;
      let frame = null;
      let view = null;
      let valueNode = null;
      let complete = false;
      const numeric = element.hasAttribute('data-count-up');
      const original = element.textContent;
      const target = Number(element.dataset.countUp);
      const finish = () => {
        complete = true;
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null;
        if (valueNode) valueNode.textContent = original;
        element.classList.remove('uni-progress-pending');
        element.classList.add('is-progress-visible');
        view?.disconnect();
      };
      if (numeric && (!Number.isFinite(target) || target < 0)) return;
      const run = () => {
        if (complete) return;
        if (!motionAllowed(element) || window.Shopify?.designMode) { finish(); return; }
        view?.disconnect();
        if (!numeric) { finish(); return; }
        const initialWidth = element.getBoundingClientRect().width;
        if (initialWidth > 0) element.style.setProperty('min-inline-size', `${initialWidth}px`);
        valueNode = document.createElement('span');
        valueNode.setAttribute('aria-hidden', 'true');
        const accessible = document.createElement('span');
        accessible.className = 'uni-visually-hidden';
        accessible.textContent = original;
        valueNode.textContent = original;
        element.replaceChildren(valueNode, accessible);
        const decimals = Number.isInteger(target) ? 0 : 1;
        const format = new Intl.NumberFormat(document.documentElement.lang || 'es', { maximumFractionDigits: decimals });
        let started = null;
        const tick = timestamp => {
          if (!element.isConnected || !motionAllowed(element)) { finish(); return; }
          if (started === null) started = timestamp;
          const elapsed = Math.min(1, (timestamp - started) / 950);
          const eased = 1 - Math.pow(1 - elapsed, 3);
          valueNode.textContent = format.format(decimals ? target * eased : Math.round(target * eased));
          if (elapsed < 1) frame = window.requestAnimationFrame(tick);
          else finish();
        };
        frame = window.requestAnimationFrame(tick);
      };
      const changed = () => { if (!motionAllowed(element)) finish(); };
      reducedMotion.addEventListener('change', changed);
      document.addEventListener('uni:motionchange', changed);
      if (typeof IntersectionObserver === 'function' && motionAllowed(element) && !window.Shopify?.designMode) {
        if (!numeric) element.classList.add('uni-progress-pending');
        view = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) run(); }, { threshold: .2 });
        view.observe(element);
      } else finish();
      figures.set(element, () => { finish(); reducedMotion.removeEventListener('change', changed); document.removeEventListener('uni:motionchange', changed); });
    });
  }

  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-motion-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: .08, rootMargin: '0px 0px -20px 0px' }) : null;

  function initReveals(scope) {
    within(scope, '.uni-catalog__grid, .uni-bento__grid, .uni-news-grid, .uni-gallery-grid').forEach(grid => {
      [...grid.children].forEach((item, index) => {
        if (!item.matches('.uni-reveal, .uni-gallery-item') || !motionAllowed(item)) return;
        item.style.setProperty('--uni-reveal-delay', `${(index % 3) * 55}ms`);
      });
    });
    within(scope, '.uni-gallery-item').forEach(item => {
      if (revealed.has(item)) return;
      revealed.add(item);
      if (!observer || !motionAllowed(item) || item.getBoundingClientRect().top < window.innerHeight * .96) return;
      item.classList.add('uni-motion-item', 'uni-motion-pending');
      observer.observe(item);
    });
  }

  function cleanup(scope) {
    [headers, heroes, carousels, figures].forEach(instances => {
      instances.forEach((dispose, element) => {
        if (scope.contains(element) || !element.isConnected) { dispose(); instances.delete(element); }
      });
    });
    within(scope, '.uni-motion-item').forEach(item => observer?.unobserve(item));
  }
  function init(scope = document) {
    within(scope, '[data-refined-header]').forEach(initHeader);
    within(scope, '[data-uni-hero-slides]').forEach(initHero);
    within(scope, '[data-uni-carousel]').forEach(initCarousel);
    initFigures(scope);
    initReveals(scope);
  }
  const sectionScripts = new Map();
  async function loadSectionScripts(scope) {
    if (!window.Shopify?.designMode) return;
    for (const source of scope.querySelectorAll('script[data-uni-section-script][src]')) {
      const readyKey = source.dataset.uniSectionScript;
      if (!readyKey || window[readyKey]) continue;
      const url = source.src;
      let pending = sectionScripts.get(url);
      if (!pending) {
        pending = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.async = false;
          script.addEventListener('load', resolve, { once: true });
          script.addEventListener('error', () => { script.remove(); reject(new Error('Section script unavailable')); }, { once: true });
          document.head.append(script);
        });
        sectionScripts.set(url, pending);
      }
      try { await pending; }
      catch { sectionScripts.delete(url); return; }
    }
  }
  document.addEventListener('transitionend', event => {
    if (event.propertyName === 'opacity' && event.target.matches('.uni-reveal.is-visible, .uni-motion-item.is-motion-visible')) event.target.classList.add('uni-motion-settled');
  });
  document.addEventListener('shopify:section:load', event => { init(event.target); loadSectionScripts(event.target); });
  document.addEventListener('shopify:section:unload', event => cleanup(event.target));
  reducedMotion.addEventListener('change', event => {
    if (!event.matches) return;
    document.querySelectorAll('.uni-motion-pending').forEach(item => {
      item.classList.add('is-motion-visible');
      observer?.unobserve(item);
    });
  });
  document.addEventListener('uni:motionchange', () => {
    if (document.body.dataset.motion !== 'none') return;
    document.querySelectorAll('.uni-motion-pending').forEach(item => { item.classList.add('is-motion-visible'); observer?.unobserve(item); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();

